export type WorkspaceNodeType =
  | "account"
  | "strategy"
  | "report"
  | "simulation"
  | "policy"
  | "combined_policy"
  | "combined_simulation";

export type WorkspaceNodeStatus =
  | "draft"
  | "processing"
  | "complete"
  | "failed"
  | "saved"
  | "published"
  | "archived";

export interface WorkspaceViewport {
  x: number;
  y: number;
  zoom: number;
}

export interface WorkspaceNodePosition {
  x: number;
  y: number;
}

/** Account tag on strategy cards / policy executing accounts (`BrokerAccountScopeDTO`). */
export interface WorkspaceNodeMetaAccount {
  brokerName: string;
  brokerAccountCode: string;
}

/** Provenance row for a combined_policy source (may be off this canvas). */
export interface WorkspaceNodeMetaSourcePolicy {
  policyVersionId: string;
  versionNumber?: number | null;
  title?: string | null;
  brokerName?: string | null;
  brokerAccountCode?: string | null;
  /** False when the source policy node is not on this workspace canvas. */
  onThisCanvas: boolean;
}

/**
 * Card-sized fields from GET /workspaces/{id} — paint canvas without N+1 detail GETs.
 * Missing stats mean “still processing / no summary”, not “fetch the entity”.
 */
export interface WorkspaceNodeMeta {
  title?: string;
  status?: WorkspaceNodeStatus;
  subtitle?: string;
  /**
   * When the canvas keeps an optimistic-* Flow id for mount continuity,
   * this holds the real workspace node id for mutations / position saves.
   */
  serverNodeId?: string;
  /** Combined policy: report whose window was adopted (first-selected source). */
  adoptedReportId?: string;
  /** Combined policy: source policy version entity ids. */
  sourcePolicyVersionIds?: string[];
  /**
   * Candle TF stamped on the node for canvas cards / Run lock.
   * Report: from report snapshot. Policy: source backtest/report (or segment finest).
   * Combined policy: finest entry-segment TFs (else source backtest).
   * Simulation: sim candle TF (often management TF when dual-TF).
   */
  candleTimeframe?: string;
  /**
   * Distinct candle TFs for the card (finest-first), e.g. multi entry-segment
   * combinations `["1m","5m"]`. Prefer for multi-chip display when length > 1.
   */
  candleTimeframes?: string[];
  /** Dual-TF simulation: entry-side candle TF when distinct from management. */
  entryCandleTimeframe?: string;
  /** Dual-TF simulation: management-side candle TF when distinct from entry. */
  managementCandleTimeframe?: string;
  /**
   * @deprecated Prefer `candleTimeframe`. Kept for FE combine-register payloads
   * and older saved meta until refresh.
   */
  resolvedCandleTimeframe?: string;
  /** Simulation stacked under another simulation (same setup, new date window). */
  isRerun?: boolean;
  /** Parent simulation backtest entity id when `isRerun` is true. */
  rerunOfEntityId?: string;
  /**
   * Policy / combined_policy created via Edit & run (fork under a source policy).
   * Canvas also infers forks from policy→policy / CP→CP edges.
   */
  isFork?: boolean;
  /** Source policy version entity id when `isFork` is true. */
  forkedFromEntityId?: string;

  /** Report type key for focus chips / filters / routing (hyphenated, e.g. trade-management). */
  reportType?: string;
  /** User-facing report type name from BE (e.g. Exit Placement). Prefer for display. */
  reportTypeLabel?: string;
  createdAt?: string;
  /** Strategy last-updated timestamp for the card “Updated” footer. */
  updatedAt?: string;
  /** Strategy: master pinescript uploaded / parsed. */
  hasMasterPine?: boolean;
  /** Strategy: TradingView / composed pinescript present. */
  hasActivePine?: boolean;
  /** Strategy: non-archived policy version count for the card chip. */
  policyVersionCount?: number;
  /**
   * Report cohort window chip — concrete YMD range when BE can resolve bounds
   * (e.g. "2025-12-01 – 2026-07-10"). Prefer over relative labels.
   * Only stamped on report nodes; sims/policies leave this null.
   */
  windowLabel?: string;
  /**
   * Original cohort preset (e.g. "all-time", "30-days") for context only —
   * not used for the calendar chip when dates exist.
   */
  timeframe?: string;
  /** Report cohort local start date (YYYY-MM-DD, reporting TZ). */
  localStartDate?: string;
  /** Report cohort local end date (YYYY-MM-DD, reporting TZ). */
  localEndDate?: string;
  /** Report cohort PnL. */
  pnl?: number | null;
  /** Report win rate (percent). */
  winRate?: number | null;
  /**
   * Report: total trades.
   * Simulation / combined_simulation: net trade-count delta (RA removals; often negative).
   * Pure Exit Placement usually 0 when `tradesChanged` is set.
   */
  tradeCount?: number | null;
  /**
   * Simulation / combined_simulation: trades whose PnL/exit changed (Exit Placement).
   * Prefer over `tradeCount` for EP impact; absent on older saved sims until re-run.
   */
  tradesChanged?: number | null;
  /** Simulation / combined_simulation PnL delta. */
  pnlChange?: number | null;
  /** Simulation / combined_simulation win-rate delta (percent points). */
  winRateDelta?: number | null;
  /**
   * Strategy: optional account tags on the card.
   * Policy / combined_policy: executing-account assignment (optional) — not the lineage home account.
   */
  accounts?: WorkspaceNodeMetaAccount[];
  /** Policy / combined_policy version number. */
  versionNumber?: number | null;
  /** True when node is a combined policy (also implied by type). */
  isCombined?: boolean;
  /** Combined policy built from sources across broker accounts. */
  isCrossAccountCombined?: boolean;
  /** Combined policy provenance (account + on-canvas flag per source). */
  sourcePolicies?: WorkspaceNodeMetaSourcePolicy[];
  /** Policy chip set: keep / remove / sl / tp (ordered). */
  policyKinds?: Array<"keep" | "remove" | "sl" | "tp">;
  /** Entry segment actions present on this policy. */
  entryActions?: Array<"keep_only" | "veto">;
  /** Exit Placement legs present. */
  exitLegs?: "none" | "sl" | "tp" | "both";
}

export interface WorkspaceSummary {
  id: string;
  name: string;
  brokerName: string;
  brokerAccountCode: string;
  updatedAt: string;
  createdAt?: string;
  nodeCount?: number;
}

export type WorkspaceViewMode = "account" | "strategy";

export interface WorkspaceNode {
  id: string;
  type: WorkspaceNodeType;
  entityId: string;
  position: WorkspaceNodePosition;
  isDisabled: boolean;
  meta?: WorkspaceNodeMeta;
  /**
   * Federated Strategy canvas provenance (null/omitted on account canvases and strategy root).
   * Use these — never `fed:…` / `fed-account:…` / `strategy-root:…` ids — for account-canvas writes and deep links.
   */
  sourceWorkspaceId?: string | null;
  sourceNodeId?: string | null;
  brokerName?: string | null;
  brokerAccountCode?: string | null;
}

/** Contributing account canvas for a Strategy federated workspace. */
export interface StrategyWorkspaceContribution {
  workspaceId: string;
  name?: string | null;
  brokerName?: string | null;
  brokerAccountCode?: string | null;
  strategyNodeId?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface StrategyWorkspacesResponse {
  strategyId: string;
  workspaces: StrategyWorkspaceContribution[];
}

/** Synthetic Strategy canvas from GET /report-strategies/{id}/federated-workspace. */
export interface FederatedWorkspaceDetail {
  strategyId: string;
  strategyName?: string | null;
  mode: "strategy";
  contributingWorkspaces: StrategyWorkspaceContribution[];
  viewport: WorkspaceViewport;
  nodes: WorkspaceNode[];
  edges: WorkspaceEdge[];
}

/** True for synthetic Strategy-view node ids (`fed:…`, `fed-account:…`, or `strategy-root:…`). */
export function isFederatedWorkspaceNodeId(nodeId: string | null | undefined): boolean {
  const id = String(nodeId ?? "").trim();
  return (
    id.startsWith("fed:") ||
    id.startsWith("fed-account:") ||
    id.startsWith("strategy-root:")
  );
}

export interface WorkspaceEdge {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
}

export interface WorkspaceDetail {
  id: string;
  name: string;
  brokerName: string;
  brokerAccountCode: string;
  viewport: WorkspaceViewport;
  nodes: WorkspaceNode[];
  edges: WorkspaceEdge[];
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkspaceCreateContext {
  workspaceId: string;
  parentNodeId: string;
  position?: WorkspaceNodePosition;
  /** Stored on the canvas node at attach time (e.g. simulation rerun badge). */
  meta?: WorkspaceNodeMeta;
}

/** Optional fields on entity create/generate payloads when adding from workspace canvas. */
export interface WorkspaceEntityCreateContext {
  workspaceId?: string;
  parentNodeId?: string;
  position?: WorkspaceNodePosition;
}

/** Hub list enrichment from BE (strategies, reports, backtests). */
export interface WorkspaceEntityLink {
  workspace_id?: string | null;
  workspace_name?: string | null;
  workspaceId?: string | null;
  workspaceName?: string | null;
}

export function resolveWorkspaceEntityLink(
  row: WorkspaceEntityLink | null | undefined
): { workspaceId: string | null; workspaceName: string | null } {
  const workspaceId = row?.workspaceId ?? row?.workspace_id ?? null;
  const workspaceName = row?.workspaceName ?? row?.workspace_name ?? null;
  return {
    workspaceId: workspaceId?.trim() ? String(workspaceId).trim() : null,
    workspaceName: workspaceName?.trim() ? String(workspaceName).trim() : null,
  };
}
